import { apiGet } from "@/lib/apiClient";
import type {
  HostingPlanId,
  OrgBillingLoadResult,
  OrgBillingSummary,
  OrgOverageSettings,
  OrgRateCard,
  RateCardLine,
} from "@/lib/billing/billingTypes";
import {
  CREDIT_USAGE_CATEGORIES,
  CREDIT_USAGE_CATEGORY_LABELS,
  isCreditSourceSystem,
  isCreditUsageCategory,
  type CreditLedgerEntry,
  type CreditRelatedResource,
  type CreditSourceSystem,
  type CreditUsageCategory,
  type CreditUsageCategoryAggregate,
} from "@/lib/billing/creditsLedger";

/**
 * Platform Credits ledger paths Directory calls (same-origin proxy to api.one.atw.net).
 * See Platform docs/CREDITS_LEDGER_API.md.
 */
export const BILLING_API = {
  credits: (orgId: string, month: string, view?: "pool" | "plan" | "usage") => {
    const qs = new URLSearchParams({ orgId, month });
    if (view && view !== "pool") qs.set("view", view);
    return `/api/billing/me/credits?${qs.toString()}`;
  },
  ledger: (orgId: string, month: string, opts?: { limit?: number; cursor?: string; category?: string }) => {
    const qs = new URLSearchParams({
      orgId,
      month,
      limit: String(opts?.limit ?? 100),
    });
    if (opts?.cursor) qs.set("cursor", opts.cursor);
    if (opts?.category) qs.set("category", opts.category);
    return `/api/billing/me/credits/ledger?${qs.toString()}`;
  },
} as const;

/** Platform CREDIT_WEIGHTS mirrored for Directory rate-card display. */
const PLATFORM_RATE_CARD_LINES: RateCardLine[] = [
  { category: "hosted_inbox", unitLabel: "mailbox-month", creditsPerUnit: 50_000 },
  { category: "storage", unitLabel: "MB", creditsPerUnit: 12, notes: "Quantity on ledger is bytes; weights apply per MB." },
  { category: "write", unitLabel: "write", creditsPerUnit: 3 },
  { category: "read", unitLabel: "read", creditsPerUnit: 1 },
  { category: "records", unitLabel: "record event", creditsPerUnit: 8 },
  { category: "automations", unitLabel: "run", creditsPerUnit: 40 },
  { category: "email_out", unitLabel: "email", creditsPerUnit: 120 },
  { category: "email_in", unitLabel: "email", creditsPerUnit: 40 },
  { category: "sms", unitLabel: "SMS", creditsPerUnit: 600 },
  { category: "voice", unitLabel: "minute", creditsPerUnit: 800 },
  { category: "ai_tokens", unitLabel: "token unit", creditsPerUnit: 2 },
  { category: "workforce_runs", unitLabel: "run", creditsPerUnit: 100 },
  { category: "sandbox_copy", unitLabel: "copy", creditsPerUnit: 5_000 },
  { category: "sandbox_use", unitLabel: "active unit", creditsPerUnit: 50 },
];

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function currentMonthYyyyMm(d = new Date()): string {
  return d.toISOString().slice(0, 7);
}

function mapHostingPlanId(tierId: string | null | undefined, planName: string): HostingPlanId {
  const raw = (tierId || planName || "").trim().toLowerCase();
  if (raw === "starter" || raw.includes("starter")) return "starter";
  if (raw === "growth" || raw.includes("growth")) return "growth";
  if (raw === "business" || raw.includes("business")) return "business";
  if (raw === "enterprise" || raw.includes("enterprise")) return "enterprise";
  return "growth";
}

function parseOverage(raw: unknown): OrgOverageSettings {
  const policy = isObject(raw) ? raw : {};
  const allowed =
    policy.creditsOverageAllowed === true ||
    policy.allowed === true ||
    policy.mode === "auto_bill_blocks";
  const hard =
    policy.creditsSegmentChargeMode === "hard_limit" ||
    policy.mode === "hard_limit" ||
    (typeof policy.creditLimit === "number" && policy.creditLimit > 0 && !allowed);

  let mode: OrgOverageSettings["mode"] = "unset";
  if (hard) mode = "hard_limit";
  else if (allowed) mode = "auto_bill_blocks";

  const blockPriceUsd =
    typeof policy.blockPriceUsd === "number"
      ? policy.blockPriceUsd
      : typeof policy.overagePer1kCreditsUsd === "number"
        ? policy.overagePer1kCreditsUsd * 25
        : 25;
  const blockSizeCredits =
    typeof policy.blockSizeCredits === "number" ? policy.blockSizeCredits : 1_000;
  const creditLimit =
    typeof policy.creditLimit === "number"
      ? policy.creditLimit
      : typeof policy.hardCreditLimit === "number"
        ? policy.hardCreditLimit
        : null;

  return {
    mode,
    blockPriceUsd,
    blockSizeCredits,
    creditLimit,
    paymentMethod: {
      brand: null,
      last4: null,
      status: "missing",
    },
  };
}

function parseRelatedResources(raw: unknown): CreditRelatedResource[] | undefined {
  if (Array.isArray(raw)) {
    const out: CreditRelatedResource[] = [];
    for (const item of raw) {
      if (typeof item === "string" && item.trim()) {
        out.push({ type: "resource", id: item.trim() });
        continue;
      }
      if (!isObject(item)) continue;
      if (typeof item.type === "string" && typeof item.id === "string") {
        out.push({
          type: item.type,
          id: item.id,
          label: typeof item.label === "string" ? item.label : undefined,
        });
      }
    }
    return out.length ? out : undefined;
  }
  return undefined;
}

function normalizeSourceSystem(raw: string): CreditSourceSystem | null {
  if (isCreditSourceSystem(raw)) return raw;
  // Legacy Directory stub / emit labels → Platform ids
  if (raw === "agents") return "ai";
  if (raw === "worksuite") return "mail";
  return null;
}

function parseLedgerEntry(row: unknown): CreditLedgerEntry | null {
  if (!isObject(row)) return null;
  const categoryRaw = typeof row.category === "string" ? row.category : "";
  if (!isCreditUsageCategory(categoryRaw)) return null;
  const sourceRaw = typeof row.sourceSystem === "string" ? row.sourceSystem : "";
  const sourceSystem = normalizeSourceSystem(sourceRaw);
  if (!sourceSystem) return null;

  const id =
    typeof row.id === "string" && row.id.trim()
      ? row.id
      : typeof row.sk === "string"
        ? row.sk
        : "";
  const orgId = typeof row.orgId === "string" ? row.orgId : "";
  const occurredAt =
    typeof row.timestamp === "string"
      ? row.timestamp
      : typeof row.occurredAt === "string"
        ? row.occurredAt
        : "";
  if (!id || !orgId || !occurredAt) return null;

  const creditsCharged = Number(
    row.creditsCharged != null ? row.creditsCharged : row.credits,
  );
  const credits = Number.isFinite(creditsCharged) ? Math.abs(creditsCharged) : 0;
  const direction =
    typeof row.direction === "string" && row.direction === "credit"
      ? "credit"
      : creditsCharged < 0
        ? "credit"
        : "debit";

  const summary =
    typeof row.reason === "string" && row.reason.trim()
      ? row.reason.trim()
      : typeof row.summary === "string" && row.summary.trim()
        ? row.summary.trim()
        : `${CREDIT_USAGE_CATEGORY_LABELS[categoryRaw]} ${direction}`;

  return {
    id,
    orgId,
    direction,
    category: categoryRaw,
    quantity: Number(row.quantity) || 0,
    credits,
    sourceSystem,
    occurredAt,
    summary,
    actorUserId: typeof row.actorUserId === "string" ? row.actorUserId : null,
    actorLabel: typeof row.actorLabel === "string" ? row.actorLabel : null,
    relatedResources: parseRelatedResources(
      row.relatedResourceIds ?? row.relatedResources,
    ),
    metadata: isObject(row.metadata)
      ? (row.metadata as CreditLedgerEntry["metadata"])
      : undefined,
  };
}

function buildUsageByCategory(
  byCategory: Record<string, number> | undefined,
  auditEntries: CreditLedgerEntry[],
): CreditUsageCategoryAggregate[] {
  const map = new Map<CreditUsageCategory, CreditUsageCategoryAggregate>();
  for (const category of CREDIT_USAGE_CATEGORIES) {
    map.set(category, {
      category,
      quantity: 0,
      credits: 0,
      eventCount: 0,
      sourceSystems: [],
    });
  }

  if (byCategory) {
    for (const [key, credits] of Object.entries(byCategory)) {
      if (!isCreditUsageCategory(key)) continue;
      const row = map.get(key)!;
      row.credits = Number(credits) || 0;
    }
  }

  for (const entry of auditEntries) {
    const row = map.get(entry.category);
    if (!row) continue;
    row.quantity += entry.quantity;
    row.eventCount += 1;
    if (!row.sourceSystems.includes(entry.sourceSystem)) {
      row.sourceSystems.push(entry.sourceSystem);
    }
    // Prefer ledger row sums when pool byCategory was empty for this key
    if (!byCategory || byCategory[entry.category] == null) {
      row.credits += entry.direction === "credit" ? -entry.credits : entry.credits;
    }
  }

  return Array.from(map.values()).sort((a, b) => b.credits - a.credits);
}

function buildRateCard(planId: HostingPlanId, lockedAt: string): OrgRateCard {
  return {
    version: "platform-ledger",
    lockedAt,
    planId,
    lines: PLATFORM_RATE_CARD_LINES,
  };
}

type LivePool = {
  orgId?: string;
  monthYyyyMm?: string;
  creditsIncluded?: number;
  creditsUsed?: number;
  creditsUsedSource?: string;
  creditsRemaining?: number;
  percentOfIncluded?: number;
  periodStartIso?: string;
  periodEndIso?: string;
  resetDateIso?: string;
  overagePolicy?: unknown;
  byCategory?: Record<string, number>;
};

type LivePlan = {
  orgId?: string;
  domain?: string | null;
  companyName?: string | null;
  plan?: string | null;
  tierId?: string;
  tierLabel?: string;
  baseFeeUsd?: number;
  includedCreditsPerMonth?: number;
  overagePer1kCreditsUsd?: number;
  overagePolicy?: unknown;
};

function assembleSummary(input: {
  orgId: string;
  orgLabel: string;
  month: string;
  pool: LivePool | null;
  plan: LivePlan | null;
  auditEntries: CreditLedgerEntry[];
}): OrgBillingSummary {
  const { pool, plan, auditEntries, month, orgId } = input;
  const planName = plan?.tierLabel || plan?.plan || "Hosting";
  const planId = mapHostingPlanId(plan?.tierId, planName);
  const periodStart = pool?.periodStartIso || `${month}-01T00:00:00.000Z`;
  const periodEnd = pool?.periodEndIso || periodStart;
  const creditsResetAt = pool?.resetDateIso || periodEnd;
  const included =
    typeof pool?.creditsIncluded === "number"
      ? pool.creditsIncluded
      : typeof plan?.includedCreditsPerMonth === "number"
        ? plan.includedCreditsPerMonth
        : 0;
  const used = typeof pool?.creditsUsed === "number" ? pool.creditsUsed : 0;
  const remaining =
    typeof pool?.creditsRemaining === "number"
      ? pool.creditsRemaining
      : Math.max(0, included - used);

  const overage = parseOverage({
    ...(isObject(plan?.overagePolicy) ? plan!.overagePolicy : {}),
    ...(isObject(pool?.overagePolicy) ? pool!.overagePolicy : {}),
    overagePer1kCreditsUsd: plan?.overagePer1kCreditsUsd,
  });

  const orgLabel =
    input.orgLabel ||
    plan?.companyName ||
    plan?.domain ||
    orgId;

  return {
    orgId: pool?.orgId || plan?.orgId || orgId,
    orgLabel,
    plan: {
      planId,
      planName,
      listPriceUsd: typeof plan?.baseFeeUsd === "number" ? plan.baseFeeUsd : null,
      rateCardVersion: "platform-ledger",
      rateCardLockedAt: periodStart,
      periodStart,
      periodEnd,
      creditsResetAt,
    },
    balance: {
      includedPool: included,
      usedCredits: used,
      remainingCredits: remaining,
      topUpCredits: 0,
    },
    usageByCategory: buildUsageByCategory(pool?.byCategory, auditEntries),
    auditEntries,
    overage,
    invoices: [],
    rateCard: buildRateCard(planId, periodStart),
    ledgerSurfaces: ["directory", "platform"],
    monthYyyyMm: pool?.monthYyyyMm || month,
    creditsUsedSource: pool?.creditsUsedSource,
  };
}

/**
 * Load org billing for Directory from live Platform Credits ledger APIs.
 * No stub fallback: failures surface as source "error".
 */
export async function loadOrgBilling(input: {
  orgId: string | null | undefined;
  orgLabel: string;
  month?: string;
}): Promise<OrgBillingLoadResult> {
  const orgId = input.orgId?.trim() || "";
  const orgLabel = input.orgLabel.trim() || "Organization";
  const month = (input.month || currentMonthYyyyMm()).trim();

  if (!orgId) {
    return {
      summary: null,
      source: "error",
      error: "No organization is selected, so Credits cannot be loaded.",
    };
  }
  if (!/^\d{4}-\d{2}$/.test(month)) {
    return {
      summary: null,
      source: "error",
      error: "Billing month must be yyyy-mm.",
    };
  }

  const [creditsRes, ledgerRes] = await Promise.all([
    apiGet<{
      configured?: boolean;
      pool?: LivePool | null;
      plan?: LivePlan | null;
      error?: string;
    }>(BILLING_API.credits(orgId, month)),
    apiGet<{
      orgId?: string;
      items?: unknown[];
      nextCursor?: string | null;
      error?: string;
    }>(BILLING_API.ledger(orgId, month, { limit: 100 })),
  ]);

  if (!creditsRes.ok) {
    return {
      summary: null,
      source: "error",
      error:
        creditsRes.error ||
        `Credits API failed (${creditsRes.status || "network"}). Try again or check Platform Billing for org ${orgId}.`,
    };
  }
  if (!ledgerRes.ok) {
    return {
      summary: null,
      source: "error",
      error:
        ledgerRes.error ||
        `Credits ledger API failed (${ledgerRes.status || "network"}). Try again or check Platform Billing for org ${orgId}.`,
    };
  }

  const pool = creditsRes.data?.pool ?? null;
  const plan = creditsRes.data?.plan ?? null;
  if (!pool && !plan) {
    return {
      summary: null,
      source: "error",
      error: `Credits API returned no pool or plan for org ${orgId}.`,
    };
  }

  const auditEntries = (ledgerRes.data?.items || [])
    .map(parseLedgerEntry)
    .filter((e): e is CreditLedgerEntry => e !== null)
    .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());

  return {
    summary: assembleSummary({
      orgId,
      orgLabel,
      month,
      pool,
      plan,
      auditEntries,
    }),
    source: "live",
  };
}
